import { FC, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Form as AntdForm } from 'antd'
import { Form, Button } from '@lmc/ui-kit'
import { styled } from 'styled-components'
import { Heading } from '@lmc/ui-kit/src/general'
import { CreateRotaPattern, HomeDto } from '@lmc/ts-common'
import useInstancesCreate from '../../hooks/useInstancesCreate'
import { useRotaPatternCreate } from '../../hooks/useRotaPatternCreate'
import { InitialState } from '../../components/ModalStepperForm'
import useRotaGenerated, { GeneratedRota } from '../../hooks/useRotaGenerated'
import GenerateEventsModal from '../components/GenerateEventsModal'
import { useSelector } from 'react-redux'
import { VISIT } from '../../constants'
import { useGroupedCarers } from 'hooks/useGroupedOptions'
import { useVisitTypesList } from 'screens/Rostering/hooks/useVisitTypesList'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useUserSkills from '../../hooks/useUserSkills'
import ResidentFormCard from './ResidentFormCard'
import InstanceFormCard from './InstanceFormCard'
import ShiftFormCard from './ShiftFormCard'
import PatternFormCard from './PatternFormCard'
import { ButtonsContainer } from 'screens/Rostering/components/ButtonsContainer'
import { showToast } from 'common/utils'

type NewRotaPatternModalProps = {
  onCancel: () => void
  type: 'visit' | 'shift'
  initialValues?: any
}

const NewRotaPatternModal: FC<NewRotaPatternModalProps> = ({
  onCancel,
  type,
  initialValues = { reoccurenceTooglePattern: true, numberSupply: 1 },
}) => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const [showGeneratePrompt, setShowGeneratePrompt] = useState(false)
  const [formData, setFormData] = useState<InitialState>(initialValues)
  const [confirmHandler, setConfirmHandler] = useState<() => void>(
    () => () => {},
  )
  const { createPattern, isLoadingCreate } = useRotaPatternCreate({
    type,
  })
  const { createEvents } = useInstancesCreate()
  const { checkRotaGenerated } = useRotaGenerated()
  const { groupedCarerOptions } = useGroupedCarers()
  const { visitTypes } = useVisitTypesList()
  const { shiftTypes } = useShiftTypesList()
  const flags = useFeatureFlags({
    flagNames: [
      'rostering_editing_limitations',
      'new_create_pattern_save_and_duplicate',
    ],
  })?.data
  const flagsRosteringEditingLimitations =
    flags?.rostering_editing_limitations?.enabled
  const flagsNewCreatePatternSaveAndDuplicate =
    flags?.new_create_pattern_save_and_duplicate?.enabled
  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home,
  )
  const { allSkills } = useUserSkills()
  const [isModalVisible, setIsModalVisible] = useState(true)
  const [duplicatedData, setDuplicatedData] = useState<any>(null)
  const initialRender = useRef(true)

  useEffect(() => {
    if (
      initialRender.current &&
      initialValues &&
      Object.keys(initialValues).length > 0
    ) {
      form.setFieldsValue(initialValues)
      setFormData((prev) => ({ ...prev, ...initialValues }))
      if (initialValues.startTime) {
        form.setFieldValue('startTime', initialValues.startTime)
      }
      if (initialValues.endTime) {
        form.setFieldValue('endTime', initialValues.endTime)
      }
      initialRender.current = false
    }
  }, [])

  const handleFinish = async (generateEvents: boolean) => {
    const pattern: any = await createPattern({
      ...formData,
    } as CreateRotaPattern)

    if (pattern && pattern._id) {
      if (generateEvents) {
        createEvents?.({
          patternId: pattern._id,
        })
      }
      return true
    }

    return false
  }

  const onFinish = async (): Promise<boolean> => {
    const checkRotaGeneratedParams: GeneratedRota = {
      patternStartDate: formData.patternStart,
      patternEndDate: formData.hasPatternEndDate
        ? formData.patternEnd
        : undefined,
    }

    const hasRota = await checkRotaGenerated(checkRotaGeneratedParams)

    if (hasRota) {
      setShowGeneratePrompt(true)

      return new Promise((resolve) => {
        const handleModalConfirm = async (generateEvents: boolean) => {
          setShowGeneratePrompt(false)
          const result = await handleFinish(generateEvents)

          if (result) {
            setIsModalVisible(false)
            onCancel()
          }

          resolve(result)
        }

        setConfirmHandler(() => handleModalConfirm)
      })
    }

    const result = await handleFinish(false)

    if (result) {
      setIsModalVisible(false)
      onCancel()
    }

    return result
  }

  const handleFormValuesChange = (values: any) => {
    setFormData((prev) => ({ ...prev, ...values }))
  }

  const handleStartTimeChanged = (value?: string) => {
    if (formData.startTime !== value) {
      setFormData?.((prev: any) => ({ ...prev, startTime: value }))
    }
  }

  const handleEndTimeChanged = (value?: string) => {
    setFormData?.((prev: any) => ({ ...prev, endTime: value }))
  }

  const handleFrequencyValue = (value?: string) => {
    setFormData?.((prev: any) => ({ ...prev, frequency: value }))
  }

  const handleIntervalValue = (value?: number) => {
    setFormData?.((prev: any) => ({ ...prev, interval: value }))
  }

  const handleDaysValue = (value?: string[]) => {
    setFormData?.((prev: any) => ({ ...prev, specificDays: value }))
  }

  const handleSaveAndDuplicate = async () => {
    try {
      await form.validateFields()

      const currentValues = form.getFieldsValue(true)

      // Check if events need to be generated
      const checkRotaGeneratedParams: GeneratedRota = {
        patternStartDate: formData.patternStart,
        patternEndDate: formData.hasPatternEndDate
          ? formData.patternEnd
          : undefined,
      }

      const hasRota = await checkRotaGenerated(checkRotaGeneratedParams)

      if (hasRota) {
        setShowGeneratePrompt(true)

        return new Promise((resolve) => {
          const handleModalConfirm = async (generateEvents: boolean) => {
            setShowGeneratePrompt(false)

            const savedPattern = await handleFinish(generateEvents)

            if (savedPattern) {
              showToast({
                title: 'Pattern created successfully',
                type: 'success',
              })

              const duplicateData = {
                ...currentValues,
                title: `${currentValues.title} (duplicate)`,
                id: undefined,
                patternId: undefined,
              }

              setDuplicatedData(duplicateData)

              setIsModalVisible(false)

              setTimeout(() => {
                setIsModalVisible(true)
              }, 100)
            }

            resolve(savedPattern)
          }

          setConfirmHandler(() => handleModalConfirm)
        })
      } else {
        const savedPattern = await handleFinish(false)

        if (savedPattern) {
          showToast({
            title: 'Pattern created successfully',
            type: 'success',
          })

          const duplicateData = {
            ...currentValues,
            title: `${currentValues.title} (duplicate)`,
            id: undefined,
            patternId: undefined,
          }

          setDuplicatedData(duplicateData)

          setIsModalVisible(false)

          setTimeout(() => {
            setIsModalVisible(true)
          }, 100)
        }
      }
    } catch (error) {
      console.error('Validation failed:', error)
    }
  }

  const handleSave = async () => {
    try {
      const success = await form
        .validateFields()
        .then(() => form.submit())
        .then(() => true)
        .catch(() => false)

      if (success) {
        return true
      }
      return false
    } catch (error) {
      console.error('Error saving pattern:', error)
      return false
    }
  }

  useEffect(() => {
    if (duplicatedData && isModalVisible) {
      form.setFieldsValue(duplicatedData)

      setDuplicatedData(null)
    }
  }, [duplicatedData, isModalVisible, form])

  return (
    <>
      <Modal
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          onCancel()
        }}
        title={<StyledHeading level={3}>{t('ADD_ROTA_PATTERN')}</StyledHeading>}
        width={648}
        footer={
          <FlexContainer>
            <ButtonsContainer>
              <Button
                key="cancel"
                onClick={() => {
                  setIsModalVisible(false)
                  onCancel()
                }}
              >
                {t('CANCEL')}
              </Button>
              {flagsNewCreatePatternSaveAndDuplicate && (
                <Button key="saveAndDuplicate" onClick={handleSaveAndDuplicate}>
                  {t('SAVE_AND_DUPLICATE')}
                </Button>
              )}
              <Button
                key="submit"
                variant="primary"
                onClick={handleSave}
                isLoading={isLoadingCreate}
                data-cy="btn-save-modal"
              >
                {t('SAVE')}
              </Button>
            </ButtonsContainer>
          </FlexContainer>
        }
      >
        <StyledForm
          form={form}
          onFinish={onFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={formData}
        >
          {type === VISIT && (
            <>
              <ResidentFormCard
                form={form}
                home={home}
                setFormData={setFormData}
                onResidentChange={(residentId) => {
                  setFormData((prev) => ({ ...prev, subjectId: residentId }))
                }}
                formData={formData}
              />
              <InstanceFormCard
                form={form}
                formData={formData}
                setFormData={setFormData}
                visitTypes={visitTypes}
                groupedCarerOptions={groupedCarerOptions}
                allSkills={allSkills}
                flagsRosteringEditingLimitations={
                  flagsRosteringEditingLimitations
                }
              />
            </>
          )}
          {type !== VISIT && (
            <ShiftFormCard
              shiftTypes={shiftTypes}
              groupedCarerOptions={groupedCarerOptions}
              formData={formData}
              form={form}
              setFormData={setFormData}
            />
          )}
          <PatternFormCard
            form={form}
            formData={formData}
            onStartTimeChanged={handleStartTimeChanged}
            onEndTimeChanged={handleEndTimeChanged}
            onFrequencyValue={handleFrequencyValue}
            onIntervalValue={handleIntervalValue}
            onDaysValue={handleDaysValue}
          />
        </StyledForm>
      </Modal>

      <GenerateEventsModal
        showPrompt={showGeneratePrompt}
        setShowPrompt={setShowGeneratePrompt}
        confirmNavigation={confirmHandler}
      />
    </>
  )
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledHeading = styled(Heading)`
  text-transform: none;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

export default NewRotaPatternModal
